import React, { Component } from 'react';
import { styled } from '@mui/material/styles';
import { isEqual, cloneDeep } from 'lodash';
import { withFormsy } from 'formsy-react';
import { DropzoneDialogBase } from 'react-mui-dropzone';
import Alert from '@mui/lab/Alert';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import PropTypes from 'prop-types';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ImageList from '@mui/material/ImageList';
import ImageListItem from '@mui/material/ImageListItem';
import ImageListItemBar from '@mui/material/ImageListItemBar';
import ListSubheader from '@mui/material/ListSubheader';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import clsx from 'clsx';
import { withTranslation } from 'react-i18next';

const PREFIX = 'ImageUploadFormsy';

const classes = {
  root: `${PREFIX}-root`,
  chip: `${PREFIX}-chip`,
  existing: `${PREFIX}-existing`,
  selected: `${PREFIX}-selected`,
};

const Root = styled('div')({
  [`&.${classes.root}`]: {
    '& > label': {
      marginBottom: 15,
    },
    '& > button': {
      marginRight: 15,
    },
  },
  [`& .${classes.chip}`]: {
    marginRight: 5,
  },
  [`& .${classes.existing}`]: {
    // existing file chips
  },
  [`& .${classes.selected}`]: {
    // selected file chips
  },
});

class ImageUploadFormsy extends Component {
  constructor(props) {
    super(props);
    this.state = {
      open: false,
      files: [],
      dataUrl: '',
    };
  }

  handleClose = () => {
    this.setState({
      open: false,
    });
  };

  handleOpen = () => {
    if (this.props.disabled) return;
    this.setState({
      open: true,
    });
  };

  getDataUrlFromFile(file) {
    if (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file.file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    } else {
      return '';
    }
  }

  handleSave = async (files) => {
    const { setValue } = this.props;
    const dataUrl = await this.getDataUrlFromFile(files[0]);
    // Saving files to state for further use and closing Modal.
    this.setState(
      {
        files: files,
        open: false,
        dataUrl,
      },
      () => {
        setValue && setValue(dataUrl);
      },
    );
  };

  handleDelete = (i) => {
    const { files } = this.state;
    files.splice(i, 1);
    this.setState({ files, open: false });
  };

  // 'https://images.unsplash.com/photo-1551963831-b3b1ca40c98e';
  renderImagePreview = () => {
    const { dataUrl } = this.state;
    const { value } = this.props;
    const url = dataUrl || value;

    if (url) {
      return (
        <ImageList sx={{ width: 250, height: 'auto' }} cols={1}>
          <ImageListItem key={url}>
            <img src={url} alt={t('org-logo')} loading="lazy" />
            <ImageListItemBar
              title={t('preview')}
              actionIcon={
                <IconButton
                  sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                  onClick={this.handleOpen}
                  aria-label={t('org-logo')}
                >
                  <EditIcon />
                </IconButton>
              }
            />
          </ImageListItem>
        </ImageList>
      );
    } else {
      return null;
    }
  };

  render() {
    const {
      extensions = [],
      label,
      name,
      disabled,
      description,
      maxFileSize = 25000,
      value,
      t,
    } = this.props;
    const { files } = this.state;
    const acceptedFiles = extensions.map((ext) => `.${ext}`);
    return (
      <Root className={classes.root}>
        {value && this.renderImagePreview(value)}

        <Button
          variant="contained"
          color="primary"
          disabled={disabled}
          onClick={this.handleOpen}
          startIcon={<CloudUploadIcon />}
        >
          {t('upload')}
        </Button>
        <DropzoneDialogBase
          onAdd={(newFile) => this.setState({ files: [].concat(newFile) })}
          onDelete={() => this.setState({ files: [] })}
          maxFileSize={maxFileSize * 1024} // number in KB
          filesMax={1}
          cancelButtonText={t('cancel')}
          submitButtonText={t('select')}
          open={this.state.open}
          onSave={() => this.handleSave(files)}
          fileObjects={files}
          acceptedFiles={acceptedFiles}
          showPreviews={true}
          onClose={this.handleClose}
        />
        <FormHelperText id="component-helper-text-file-types">{`${t('allowed-file-types')}: ${extensions.join(', ')}`}</FormHelperText>
        {description && <FormHelperText id="component-helper-text">{description}</FormHelperText>}
      </Root>
    );
  }
}

ImageUploadFormsy.propTypes = {
  extensions: PropTypes.arrayOf(PropTypes.string),
  disabled: PropTypes.boolean,
  filesMax: PropTypes.number,
  filesMin: PropTypes.number,
  label: PropTypes.string,
  description: PropTypes.string,
  setValue: PropTypes.func,
  classes: PropTypes.object,
  value: PropTypes.string,
};

const StyledImageUploadFormsy = ImageUploadFormsy;

export default React.memo(withFormsy(withTranslation('default')(StyledImageUploadFormsy)));
